import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScanLine, Camera, X, Search, MapPin, Loader2, ScanBarcode } from 'lucide-react';
import { toast } from 'sonner';

const CONTAINER_ID = 'qr-reader-container';

export default function QRScanner({ onScanResult, buttonLabel = 'Scansiona QR', buttonVariant = 'outline', buttonSize = 'sm', className = '', testId = 'qr-scanner-btn' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('choose'); // 'choose' | 'camera' | 'manual' | 'scanner'
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [manualId, setManualId] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [scannerInput, setScannerInput] = useState('');
  const [scannerReady, setScannerReady] = useState(true);
  const scannerRef = useRef(null);
  const searchTimer = useRef(null);
  const scannerInputRef = useRef(null);

  const handleResult = useCallback(async (text) => {
    // 1. Match URL pattern: /ospiti/UUID
    const ospiteMatch = text.match(/\/ospiti\/([a-f0-9-]+)/i);
    if (ospiteMatch) {
      if (onScanResult) { onScanResult(ospiteMatch[1]); }
      else { toast.success('Ospite identificato'); navigate(`/m/ospite/${ospiteMatch[1]}`); }
      return;
    }
    // 2. Match raw UUID (36 chars)
    const uuidMatch = text.match(/^[a-f0-9-]{36}$/i);
    if (uuidMatch) {
      if (onScanResult) { onScanResult(text.toLowerCase()); }
      else { toast.success('Ospite identificato'); navigate(`/m/ospite/${text.toLowerCase()}`); }
      return;
    }
    // 3. Try extracting any UUID-like pattern from the text
    const uuidExtract = text.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    if (uuidExtract) {
      const id = uuidExtract[1].toLowerCase();
      if (onScanResult) { onScanResult(id); }
      else { toast.success('Ospite identificato'); navigate(`/m/ospite/${id}`); }
      return;
    }
    // 4. Fallback: try API lookup by the scanned text (could be an ID, partial URL, etc.)
    try {
      const res = await api.get(`/ospiti/cerca?q=${encodeURIComponent(text.trim())}`);
      if (res.data && res.data.length === 1) {
        const osp = res.data[0];
        if (onScanResult) { onScanResult(osp.id); }
        else { toast.success(`Ospite trovato: ${osp.cognome} ${osp.nome}`); navigate(`/ospiti/${osp.id}`); }
        return;
      } else if (res.data && res.data.length > 1) {
        toast.info(`Trovati ${res.data.length} ospiti. Usa la ricerca manuale.`);
        return;
      }
    } catch {}
    // 5. Nothing matched
    if (onScanResult) { onScanResult(text); }
    else { toast.error(`QR code non riconosciuto. Contenuto letto: "${text.substring(0, 60)}"`); }
  }, [onScanResult, navigate]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      try { scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError('');
    setScanning(false);
    await stopScanner();
    await new Promise(r => setTimeout(r, 300));
    const container = document.getElementById(CONTAINER_ID);
    if (!container) { setError('Container video non trovato'); return; }
    try {
      const scanner = new Html5Qrcode(CONTAINER_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decoded) => {
          scanner.stop().then(() => { scanner.clear(); scannerRef.current = null; }).catch(() => {});
          setOpen(false);
          setMode('choose');
          handleResult(decoded);
        },
        () => {}
      );
      setScanning(true);
    } catch (err) {
      const msg = err?.message || String(err);
      if (msg.includes('Permission') || msg.includes('NotAllowed')) setError('Permesso fotocamera negato. Abilita la fotocamera nelle impostazioni del browser.');
      else if (msg.includes('NotFound')) setError('Nessuna fotocamera trovata.');
      else setError(`Errore fotocamera: ${msg}`);
    }
  }, [stopScanner, handleResult]);

  useEffect(() => {
    if (open && mode === 'camera') { const t = setTimeout(startCamera, 200); return () => clearTimeout(t); }
    return () => { stopScanner(); };
  }, [open, mode, startCamera, stopScanner]);

  // Keep scanner input focused when in scanner mode
  useEffect(() => {
    if (open && mode === 'scanner') {
      const t = setTimeout(() => { scannerInputRef.current?.focus(); }, 100);
      return () => clearTimeout(t);
    }
  }, [open, mode]);

  const handleClose = () => { stopScanner(); setOpen(false); setMode('choose'); setManualId(''); setScannerInput(''); setError(''); setSearchResults([]); setScannerReady(true); };

  // Handle physical scanner input — rapid keystrokes ending with Enter
  const handleScannerKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = scannerInput.trim();
      if (!val) return;
      setScannerReady(false);
      setOpen(false);
      setScannerInput('');
      setMode('choose');
      setScannerReady(true);
      handleResult(val);
    }
  };

  // Search by name/CF when input changes
  const handleSearchInput = (value) => {
    setManualId(value);
    setSearchResults([]);

    if (/^[a-f0-9-]{20,}$/.test(value.trim())) return;

    if (value.trim().length >= 2) {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(async () => {
        setSearching(true);
        try {
          const res = await api.get(`/ospiti/cerca?q=${encodeURIComponent(value.trim())}`);
          setSearchResults(res.data || []);
        } catch {
          setSearchResults([]);
        }
        setSearching(false);
      }, 350);
    }
  };

  const selectOspite = (ospite) => {
    setOpen(false);
    setManualId('');
    setSearchResults([]);
    setMode('choose');
    if (onScanResult) { onScanResult(ospite.id); }
    else { toast.success(`${ospite.cognome} ${ospite.nome}`); navigate(`/ospiti/${ospite.id}`); }
  };

  const handleManualSubmit = () => {
    if (!manualId.trim()) return;
    setOpen(false);
    const id = manualId.trim();
    setManualId('');
    setMode('choose');
    setSearchResults([]);
    handleResult(id);
  };

  return (
    <>
      <Button variant={buttonVariant} size={buttonSize} onClick={() => { setMode('choose'); setError(''); setOpen(true); }} className={className} data-testid={testId}>
        <ScanLine className="w-4 h-4 mr-1" />{buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-md" data-testid="qr-scanner-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-brand" />Identifica Ospite
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">

            {/* MODE: CHOOSE */}
            {mode === 'choose' && (
              <div className="space-y-3 py-2">
                <p className="text-sm text-slate-500 text-center">Scegli come identificare l'ospite</p>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setMode('scanner')}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-orange-200 rounded-xl hover:border-orange-400 hover:bg-orange-50/50 transition-all bg-orange-50/30"
                    data-testid="qr-choose-scanner"
                  >
                    <ScanBarcode className="w-8 h-8 text-orange-500" />
                    <span className="text-sm font-medium text-slate-700">Lettore</span>
                    <span className="text-[10px] text-slate-400">Scanner fisico</span>
                  </button>
                  <button
                    onClick={() => setMode('camera')}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-slate-200 rounded-xl hover:border-brand hover:bg-blue-50/50 transition-all"
                    data-testid="qr-choose-camera"
                  >
                    <Camera className="w-8 h-8 text-blue-500" />
                    <span className="text-sm font-medium text-slate-700">Fotocamera</span>
                    <span className="text-[10px] text-slate-400">Scansiona QR</span>
                  </button>
                  <button
                    onClick={() => setMode('manual')}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-slate-200 rounded-xl hover:border-emerald-400 hover:bg-emerald-50/50 transition-all"
                    data-testid="qr-choose-manual"
                  >
                    <Search className="w-8 h-8 text-emerald-500" />
                    <span className="text-sm font-medium text-slate-700">Cerca</span>
                    <span className="text-[10px] text-slate-400">Nome o CF</span>
                  </button>
                </div>
              </div>
            )}

            {/* MODE: SCANNER (Physical barcode/QR reader) */}
            {mode === 'scanner' && (
              <div className="space-y-4 py-2">
                <div className="text-center space-y-2">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-100 mb-1">
                    <ScanBarcode className="w-8 h-8 text-orange-600 animate-pulse" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">Lettore Fisico Attivo</p>
                  <p className="text-xs text-slate-400">Scansiona il codice a barre o QR code con il lettore USB/Bluetooth</p>
                </div>
                <div className="relative">
                  <Input
                    ref={scannerInputRef}
                    value={scannerInput}
                    onChange={e => setScannerInput(e.target.value)}
                    onKeyDown={handleScannerKeyDown}
                    onBlur={() => { setTimeout(() => { scannerInputRef.current?.focus(); }, 50); }}
                    placeholder="In attesa di scansione..."
                    className="text-center text-lg font-mono border-2 border-orange-300 focus:border-orange-500 bg-orange-50/50 h-14"
                    data-testid="qr-scanner-physical-input"
                    autoFocus
                    autoComplete="off"
                  />
                  {scannerReady && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                  Il campo cattura automaticamente il codice dal lettore. Assicurati che il cursore sia nel campo sopra.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setMode('camera'); setError(''); setScannerInput(''); }} className="text-xs flex-1">
                    <Camera className="w-3.5 h-3.5 mr-1" />Fotocamera
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setMode('manual'); setScannerInput(''); }} className="text-xs flex-1">
                    <Search className="w-3.5 h-3.5 mr-1" />Cerca Manuale
                  </Button>
                </div>
              </div>
            )}

            {/* MODE: CAMERA */}
            {mode === 'camera' && (
              <>
                <p className="text-xs text-slate-500 text-center">Inquadra il QR code dell'ospite</p>
                <div id={CONTAINER_ID} className="rounded-lg overflow-hidden bg-black min-h-[280px] flex items-center justify-center" data-testid="qr-scanner-view">
                  {!scanning && !error && (
                    <div className="text-center text-white/60">
                      <Camera className="w-8 h-8 mx-auto mb-2" /><p className="text-xs">Avvio fotocamera...</p>
                    </div>
                  )}
                </div>
                {error && (
                  <div className="bg-red-50 text-red-600 text-xs p-3 rounded-lg text-center space-y-2">
                    <p>{error}</p>
                    <div className="flex gap-2 justify-center">
                      <Button size="sm" variant="outline" onClick={() => { stopScanner(); setMode('scanner'); }} className="text-xs">
                        <ScanBarcode className="w-3.5 h-3.5 mr-1" />Usa Lettore Fisico
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { stopScanner(); setMode('manual'); }} className="text-xs">
                        <Search className="w-3.5 h-3.5 mr-1" />Cerca Manuale
                      </Button>
                    </div>
                  </div>
                )}
                {!error && (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { stopScanner(); setMode('scanner'); }} className="text-xs text-slate-400 flex-1">
                      <ScanBarcode className="w-3.5 h-3.5 mr-1" />Lettore Fisico
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { stopScanner(); setMode('manual'); }} className="text-xs text-slate-400 flex-1">
                      <Search className="w-3.5 h-3.5 mr-1" />Cerca Manuale
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* MODE: MANUAL - Enhanced with name/CF search */}
            {mode === 'manual' && (
              <div className="space-y-3 py-2">
                <p className="text-xs text-slate-500 text-center">Cerca per nome, cognome, codice fiscale o ID ospite</p>
                <div>
                  <Label className="text-xs">Cerca Ospite</Label>
                  <Input
                    value={manualId}
                    onChange={e => handleSearchInput(e.target.value)}
                    placeholder="Es. Marchetti, MRCGLN38D45..., o ID"
                    data-testid="qr-manual-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        if (searchResults.length === 1) selectOspite(searchResults[0]);
                        else handleManualSubmit();
                      }
                    }}
                    autoFocus
                  />
                </div>

                {searching && (
                  <div className="flex items-center justify-center py-3 text-slate-400 text-xs">
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Ricerca in corso...
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="space-y-1.5 max-h-[240px] overflow-y-auto" data-testid="qr-search-results">
                    {searchResults.map(osp => (
                      <button
                        key={osp.id}
                        onClick={() => selectOspite(osp)}
                        className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left"
                        data-testid={`qr-search-result-${osp.id}`}
                      >
                        <Avatar className="w-9 h-9">
                          <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                            {(osp.cognome?.[0] || '') + (osp.nome?.[0] || '')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{osp.cognome} {osp.nome}</p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                            {osp.stanza && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />Camera {osp.stanza}/{osp.letto}</span>}
                            {osp.codice_fiscale && <span className="font-mono">{osp.codice_fiscale.substring(0, 6)}...</span>}
                          </div>
                        </div>
                        <Badge className={`text-[9px] ${osp.stato === 'presente' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {osp.stato || 'presente'}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}

                {!searching && manualId.trim().length >= 2 && searchResults.length === 0 && !/^[a-f0-9-]{20,}$/.test(manualId.trim()) && (
                  <p className="text-xs text-slate-400 text-center py-2">Nessun ospite trovato</p>
                )}

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setMode('scanner'); setError(''); setSearchResults([]); }} className="text-xs flex-1">
                    <ScanBarcode className="w-3.5 h-3.5 mr-1" />Lettore
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setMode('camera'); setError(''); setSearchResults([]); }} className="text-xs flex-1">
                    <Camera className="w-3.5 h-3.5 mr-1" />Fotocamera
                  </Button>
                  <Button size="sm" onClick={handleManualSubmit} disabled={!manualId.trim()} className="bg-brand hover:bg-brand-dark text-white text-xs flex-1" data-testid="qr-manual-submit">
                    <Search className="w-3.5 h-3.5 mr-1" />Cerca
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleClose}><X className="w-4 h-4 mr-1" />Chiudi</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
